export const LEGACY_ROUTING_STATUS_HEADERS = [
  "PO Number",
  "Load ID",
  "Load Dest",
  "Load Dest Type",
  "Load Dest Address",
  "Load P/U Window Start",
  "Load P/U Window End",
  "MABD",
  "Carrier PU Date",
  "Carrier Due Date",
  "Carrier Name",
  "Industry SCAC",
  "Mode",
  "Ship Point",
  "Loading Method",
  "Cases",
  "Weight",
  "Cube",
  "Pallets",
  "Event Code",
  "PO Type",
  "Department",
  "Confirmed Date",
  "Held/Suspended",
  "PO Dest",
];

export const STRIDE_INFORMATION_HEADERS = ["Shipment #", "Fleet Load ID"];

export const REQUIRED_STRIDE_HEADERS = [
  "PO",
  "Load #",
  "Load Destination ID",
  "Load Destination Type",
  "Load Destination Address",
  "MABD",
  "Carrier PU Date",
  "Carrier Due Date",
  "Carrier Name",
  "Industry SCAC",
  "Ship Point ID",
  "Routing Method",
  "Total Cases",
  "Total Weight (lbs)",
  "Total Cube",
  "Total Pallets",
  "Event Code",
  "PO Type",
  "Department #",
  "Shipment Create Date",
  "Destination ID & Name",
];

const STRIDE_SIGNATURE_HEADERS = [
  "Shipment #",
  "PO",
  "Load #",
  "Routing Method",
  "Shipment Create Date",
  "Destination ID & Name",
];

const MONTHS = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const cleanHeader = (value) => String(value ?? "").trim();
export const normalizeRoutingLoadId = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "0";
};

export const canonicalRoutingWorkbookIdentity = (value, field) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean" || value instanceof Date) {
    throw new Error(`Import failed: unsupported ${field} value.`);
  }
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) throw new Error(`Import failed: unsupported ${field} value.`);
    return String(value);
  }
  if (typeof value !== "string") throw new Error(`Import failed: unsupported ${field} value.`);
  const normalized = value.normalize("NFC").trim();
  if (normalized.includes("\u0000") || normalized.startsWith("=")) {
    throw new Error(`Import failed: unsupported ${field} value.`);
  }
  if (field === "Load ID" && normalized === "-/-") return "";
  return normalized;
};

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "-/-" ? "" : trimmed;
  }
  return value;
};
const textValue = (value) => {
  const cleaned = cleanValue(value);
  return cleaned === "" ? "" : String(cleaned);
};

const isSameHeaderRow = (headers, expected) =>
  headers.length === expected.length && headers.every((header, index) => header === expected[index]);

const pad2 = (value) => String(value).padStart(2, "0");

const isValidDateParts = (month, day, year) => {
  if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const dateError = (field, sourceRowNumber, value) =>
  new Error(
    `Import failed: invalid ${field} at source row ${sourceRowNumber}: "${String(value)}".`
  );

export const parseStrideDate = (value, field, sourceRowNumber) => {
  const cleaned = cleanValue(value);
  if (cleaned === "") return "";
  if (typeof cleaned !== "string") throw dateError(field, sourceRowNumber, value);

  const namedDateMatch = cleaned.match(/^(\d{1,2})\s+([A-Za-z]{3}),\s*(\d{4})$/);
  if (namedDateMatch) {
    const day = Number(namedDateMatch[1]);
    const month = MONTHS[namedDateMatch[2].toLowerCase()];
    const year = Number(namedDateMatch[3]);
    if (!month || !isValidDateParts(month, day, year)) {
      throw dateError(field, sourceRowNumber, value);
    }
    return `${pad2(month)}/${pad2(day)}/${year}`;
  }

  const numericDateMatch = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (numericDateMatch) {
    const month = Number(numericDateMatch[1]);
    const day = Number(numericDateMatch[2]);
    const year = Number(numericDateMatch[3]);
    if (!isValidDateParts(month, day, year)) {
      throw dateError(field, sourceRowNumber, value);
    }
    return `${pad2(month)}/${pad2(day)}/${year}`;
  }

  throw dateError(field, sourceRowNumber, value);
};

export const parseStrideShipmentCreateDate = (value, field, sourceRowNumber) => {
  const cleaned = cleanValue(value);
  if (cleaned === "") return "";
  if (typeof cleaned !== "string") throw dateError(field, sourceRowNumber, value);

  const match = cleaned.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?\s*\([A-Za-z]{1,5}\))?$/
  );
  if (!match) throw dateError(field, sourceRowNumber, value);
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!isValidDateParts(month, day, year)) throw dateError(field, sourceRowNumber, value);
  return `${pad2(month)}/${pad2(day)}/${year}`;
};

const parseLeadingNumericId = (value, field, sourceRowNumber, poNumber) => {
  const cleaned = cleanValue(value);
  if (cleaned === "") return "";
  const match = String(cleaned).match(/^(\d+)(?:\b|-)/);
  if (!match) {
    const poLabel = poNumber ? ` for PO ${poNumber}` : "";
    throw new Error(
      `Import failed: invalid ${field} at source row ${sourceRowNumber}${poLabel}: "${String(value)}".`
    );
  }
  return match[1];
};

const numericValue = (value) => {
  const cleaned = cleanValue(value);
  if (cleaned === "") return "";
  if (typeof cleaned === "number") return cleaned;
  const normalized = String(cleaned).replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : cleaned;
};

const buildHeaderIndex = (headers) =>
  new Map(headers.map((header, index) => [header, index]));

export const detectRoutingStatusFormat = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return "empty";
  const headers = (rows[0] || []).map(cleanHeader);
  while (headers.length && headers[headers.length - 1] === "") headers.pop();
  if (isSameHeaderRow(headers, LEGACY_ROUTING_STATUS_HEADERS)) return "legacy";
  const present = new Set(headers);
  if (REQUIRED_STRIDE_HEADERS.every((header) => present.has(header))) return "stride";
  if (STRIDE_SIGNATURE_HEADERS.some((header) => present.has(header))) return "incomplete-stride";
  return "unknown";
};

const sourceInformationValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

export const canonicalizeRoutingWorkbookIdentityRows = (rows) => {
  const format = detectRoutingStatusFormat(rows);
  if (!Array.isArray(rows) || !rows.length || !["legacy", "stride"].includes(format)) return rows;
  const headers = (rows[0] || []).map(cleanHeader);
  const poIndex = format === "legacy" ? 0 : headers.indexOf("PO");
  const loadIndex = format === "legacy" ? 1 : headers.indexOf("Load #");
  return rows.map((row, index) => {
    if (index === 0) return row;
    const copy = [...(row || [])];
    copy[poIndex] = canonicalRoutingWorkbookIdentity(copy[poIndex], "PO Number");
    copy[loadIndex] = canonicalRoutingWorkbookIdentity(copy[loadIndex], "Load ID");
    return copy;
  });
};

export const adaptRoutingStatusRows = (rows) => {
  const format = detectRoutingStatusFormat(rows);
  if (format === "empty") throw new Error("Import failed: file does not contain any rows.");
  if (format === "legacy") return rows;

  const sourceHeaders = (rows[0] || []).map(cleanHeader);
  const headerIndex = buildHeaderIndex(sourceHeaders);
  if (format === "incomplete-stride") {
    const missing = REQUIRED_STRIDE_HEADERS.filter((header) => !headerIndex.has(header));
    throw new Error(`Import failed: Stride file is missing required columns: ${missing.join(", ")}.`);
  }
  if (format !== "stride") return rows;

  const get = (row, header) => cleanValue(row?.[headerIndex.get(header)]);
  const informationHeaders = STRIDE_INFORMATION_HEADERS.filter((header) => headerIndex.has(header));
  const converted = rows.slice(1).map((row, dataIndex) => {
    const sourceRowNumber = dataIndex + 2;
    const poNumber = canonicalRoutingWorkbookIdentity(get(row, "PO"), "PO Number");
    const operationalValues = [
      poNumber,
      canonicalRoutingWorkbookIdentity(get(row, "Load #"), "Load ID"),
      textValue(get(row, "Load Destination ID")),
      textValue(get(row, "Load Destination Type")),
      textValue(get(row, "Load Destination Address")),
      "",
      "",
      parseStrideDate(get(row, "MABD"), "MABD", sourceRowNumber),
      parseStrideDate(get(row, "Carrier PU Date"), "Carrier PU Date", sourceRowNumber),
      parseStrideDate(get(row, "Carrier Due Date"), "Carrier Due Date", sourceRowNumber),
      textValue(get(row, "Carrier Name")),
      textValue(get(row, "Industry SCAC")),
      textValue(get(row, "Mode")),
      parseLeadingNumericId(get(row, "Ship Point ID"), "Ship Point ID", sourceRowNumber, poNumber),
      textValue(get(row, "Routing Method")),
      numericValue(get(row, "Total Cases")),
      numericValue(get(row, "Total Weight (lbs)")),
      numericValue(get(row, "Total Cube")),
      numericValue(get(row, "Total Pallets")),
      textValue(get(row, "Event Code")),
      textValue(get(row, "PO Type")),
      textValue(get(row, "Department #")),
      parseStrideShipmentCreateDate(
        get(row, "Shipment Create Date"),
        "Shipment Create Date",
        sourceRowNumber
      ),
      "NO",
      parseLeadingNumericId(
        get(row, "Destination ID & Name"),
        "Destination ID & Name",
        sourceRowNumber,
        poNumber
      ),
    ];
    return [
      ...operationalValues,
      ...informationHeaders.map((header) =>
        sourceInformationValue(row?.[headerIndex.get(header)])
      ),
    ];
  });

  return [[...LEGACY_ROUTING_STATUS_HEADERS, ...informationHeaders], ...converted];
};
